/* eslint-disable react-hooks/rules-of-hooks */
import { User } from "@iventis/domain-model/model/user";
import { styled } from "@iventis/styles";
import { Content } from "@iventis/translations/content/typed-content";
import { LinearProgress, Tab, Tabs } from "@mui/material";
import React, { ReactNode, useMemo, useRef, useState } from "react";
import { useMachine } from "@xstate/react";
import { UseIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { FormWizardTemplate, Stage } from "@iventis/components";
import { Toasts } from "@iventis/toasts/src/toast.types";
import { PeopleSelector } from "./people-selector";
import { peopleViewMachine as _peopleViewMachine, PeopleViewMachineEventType, UsePeopleViewMachineType } from "./people-view.machine";
import { PeopleApiCallbacks } from "./types";

export const AddPeople: React.FC<{
    addPeople: (people: User[]) => void;
    title: string | ReactNode;
    apiCallbacks: Pick<PeopleApiCallbacks, "postNewUserAndGroups" | "getTeams" | "getPeople">;
    unavailablePersonIds?: string[];
    className?: string;
    isSubmitting?: boolean;
    projectId?: string;
    machineInstance?: UsePeopleViewMachineType;
    confirmButtonText?: string;
    translate: UseIventisTranslate;
    toast: Toasts;
    extraStages?: Stage[];
}> = ({ addPeople, apiCallbacks, title, unavailablePersonIds, className, machineInstance, projectId, isSubmitting, confirmButtonText, translate, toast, extraStages = [] }) => {
    if (machineInstance === undefined && projectId === undefined) {
        throw new Error("Must supply either machine instance or project id");
    }

    const { postNewUserAndGroups, getTeams, getPeople } = apiCallbacks;

    const { current: peopleViewMachine } = useRef(_peopleViewMachine({ postNewUserAndGroups, getTeams, getPeople, toast, translate }));

    const [state, send] = machineInstance ?? useMachine(peopleViewMachine, { context: { projectId } });

    const selectedPeople = useMemo(() => state.context.selectedPeople.map((personId) => state.context.people.find((person) => person.id === personId)), [
        state.context.selectedPeople,
    ]);

    const [tab, setTab] = useState(0);

    const stages: Stage[] = [
        {
            primaryButtonText: confirmButtonText !== undefined ? confirmButtonText : translate(Content.people.addSelectedToTeam),
            primaryButtonCallback: () => {
                addPeople(selectedPeople);
            },
            secondaryButtons: [{ buttonText: translate(Content.common.buttons.cancel), onButtonPressed: () => addPeople([]) }],
            isValid: selectedPeople.length > 0,
        },
        ...extraStages,
    ];

    return (
        <FormWizardTemplate title={title} stages={stages} currentStage={0} className={className} classNameContent={className} isSubmitting={isSubmitting}>
            <Tabs variant="fullWidth" indicatorColor="primary" value={tab} onChange={(_, newValue) => setTab(newValue)}>
                <Tab label={translate(Content.people.allPeople).toUpperCase()} />
                <Tab label={translate(Content.common.selected, { selectedNumber: selectedPeople.length }).toUpperCase()} />
            </Tabs>
            {state.matches("peopleLoading") && <LinearProgress />}
            {state.matches("idle") && (
                <>
                    {tab === 0 && (
                        <StyledTabContents>
                            <PeopleSelector
                                people={state.context.people}
                                selectedPeople={selectedPeople}
                                unavailablePersonIds={unavailablePersonIds}
                                onChange={(person, checked) => send({ type: PeopleViewMachineEventType.UPDATE_SELECTION, payload: { ids: [person.id], selected: checked } })}
                            />
                        </StyledTabContents>
                    )}
                    {tab === 1 && (
                        <StyledTabContents>
                            <PeopleSelector
                                people={selectedPeople}
                                selectedPeople={selectedPeople}
                                onChange={(person, checked) => send({ type: PeopleViewMachineEventType.UPDATE_SELECTION, payload: { ids: [person.id], selected: checked } })}
                            />
                        </StyledTabContents>
                    )}
                </>
            )}
        </FormWizardTemplate>
    );
};

const StyledTabContents = styled.section`
    flex-grow: 1;
    overflow-y: auto;
`;
